import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarDays, PhoneCall, RefreshCw } from "lucide-react";
import EventRow from "@/components/calendar/EventRow";
import { fmtDateLong, fmtTime } from "@/lib/calendar";

const UnassignedAlert = ({ items, onOpen }) => (
  <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 mb-4" data-testid="unassigned-warning">
    <p className="font-bold text-red-700 flex items-center gap-2">
      <AlertTriangle className="w-5 h-5" />
      {items.length} approved ride{items.length > 1 ? "s have" : " has"} no driver assigned
    </p>
    <div className="mt-2 flex flex-wrap gap-2">
      {items.slice(0, 6).map((e) => (
        <button key={e.id} type="button" onClick={() => onOpen(e.ride_id)}
          className="text-xs bg-white border border-red-200 rounded-lg px-2 py-1 hover:bg-red-100 transition-colors duration-150"
          data-testid={`unassigned-item-${e.ride_id}-${e.leg}`}>
          {e.date} {fmtTime(e.time)} · {e.client_name}{e.leg === "return" ? " (return)" : ""}
        </button>
      ))}
      {items.length > 6 && <span className="text-xs text-red-700 self-center">+{items.length - 6} more</span>}
    </div>
  </div>
);

export const TodayRidesPanel = ({ onOpenRide, onNewBooking, refreshKey = 0 }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/dispatch/today");
      setData(res.data);
    } catch (e) {
      console.error("Failed to load today's rides", e);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load, refreshKey]);

  const upcoming = (data?.events || []).filter((e) => e.status !== "completed");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 mb-6 shadow-sm" data-testid="today-rides-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-heading font-black text-slate-900 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-cyan-600" /> Today's Rides
          </h2>
          <p className="text-xs text-slate-500">{data ? fmtDateLong(data.today) : ""} · Eastern Time · {upcoming.length} upcoming pickup(s)</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="today-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <Button size="sm" onClick={onNewBooking} className="bg-slate-900 hover:bg-slate-800" data-testid="new-phone-booking-btn">
            <PhoneCall className="w-4 h-4 mr-2" /> New Phone Booking
          </Button>
        </div>
      </div>
      {data?.unassigned?.length > 0 && <UnassignedAlert items={data.unassigned} onOpen={onOpenRide} />}
      {data && data.events.length === 0 && <p className="text-sm text-slate-500 py-4" data-testid="today-empty">No pickups scheduled for today.</p>}
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="today-rides-list">
        {(data?.events || []).map((e) => <EventRow key={e.id} event={e} onOpen={onOpenRide} />)}
      </div>
    </div>
  );
};

export default TodayRidesPanel;
